import {
  IconCircleCheckFilled,
  IconExclamationCircleFilled,
  IconHelpOctagonFilled,
  IconInfoCircleFilled,
} from '@tabler/icons-react';
import clsx from 'clsx';
import React, { CSSProperties, ReactNode } from 'react';

import Select, { GroupBase, OptionProps, SingleValueProps, components } from 'react-select';

import { CollateralStatus } from '__generated__/globalTypes';
export type FileDiligenceStatus =
  | 'File Required'
  | 'File Received'
  | 'In Progress'
  | 'Additional QA Required'
  | 'File Reviewed'
  | 'File Rejected';

interface StatusIconMap {
  [key: string]: ReactNode;
}

const statusIconMap: StatusIconMap = {
  'File Required': <IconHelpOctagonFilled color="#F2994A" />,
  'File Received': <IconInfoCircleFilled color="#8B98F9" />,
  'In Progress': <IconInfoCircleFilled color="#8B98F9" />,
  'Additional QA Required': <IconHelpOctagonFilled color="#F2994A" />,
  'File Reviewed': <IconCircleCheckFilled color="#9BDB72" />,
  'File Rejected': <IconExclamationCircleFilled color="#EB5757" />,
};

interface FileStatusLabelProps {
  status: FileDiligenceStatus;
  [key: string]: unknown;
}

const FileStatusLabel: React.FC<FileStatusLabelProps> = ({ status, ...props }) => {
  return (
    // items-end seems to be the only way to get the icon to align with the text
    <span {...props} className="inline-flex items-end gap-2">
      {statusIconMap[status]}
      {status}
    </span>
  );
};

export { FileStatusLabel };

interface FileStatusDropdownProps {
  value: CollateralStatus | null;
  onChange?: (value: FileDiligenceStatus) => void;
  isInSelectedRow?: boolean;
  borderColor?: string;
  isDisabled?: boolean;
}

export const FileStatusDropdown = ({
  isInSelectedRow,
  onChange,
  value,
  borderColor,
  isDisabled = false,
}: FileStatusDropdownProps) => {
  if (value === null) return null;

  const convertedValue = fromCollateralStatus(value);

  return (
    <span
      onClick={(e) => e.stopPropagation()}
      // borderColor is an arbitrary runtime value, so it reaches the control through a CSS variable
      style={borderColor ? ({ '--file-status-border': borderColor } as CSSProperties) : undefined}
    >
      <Select
        unstyled
        isMulti={false}
        isSearchable={false}
        isClearable={false}
        isDisabled={isDisabled}
        menuPortalTarget={document.body}
        menuPosition={'fixed'}
        options={options}
        value={options.find((option) => option.value === convertedValue)}
        onChange={(option) => {
          onChange && onChange((option as FileStatusOptionType).value);
        }}
        components={{
          Option: CustomOption,
          SingleValue: CustomSingleValue,
        }}
        classNames={{
          control: (state) =>
            clsx(
              '!min-h-30px rounded border border-solid px-2 py-0 text-left text-sm',
              state.hasValue ? 'text-slate-50' : 'text-slate-400',
              borderColor
                ? 'border-[color:var(--file-status-border)]'
                : state.menuIsOpen
                  ? isInSelectedRow
                    ? 'border-white'
                    : 'border-pink-700'
                  : 'border-transparent',
              isInSelectedRow ? 'hover:border-white' : 'hover:border-pink-700',
            ),
          singleValue: () => 'leading-none',
          dropdownIndicator: () => (isInSelectedRow ? 'text-white' : 'text-accent-default'),
          menuPortal: () => '!z-100',
          menu: () =>
            '!z-100 mt-0.5 overflow-hidden rounded-md border border-solid border-pink-700 bg-background-surface py-0.5 text-sm text-white shadow-select-menu',
          option: (state) =>
            clsx('px-3 py-1.5 leading-none', state.isFocused ? 'bg-pink-700' : 'bg-transparent'),
        }}
      />
    </span>
  );
};

interface FileStatusOptionType {
  value: FileDiligenceStatus;
  label: FileDiligenceStatus;
}

const statuses: FileDiligenceStatus[] = [
  'File Required',
  'File Received',
  'In Progress',
  'Additional QA Required',
  'File Reviewed',
  'File Rejected',
];
const options: FileStatusOptionType[] = statuses.map((status) => ({
  value: status,
  label: status,
}));

const CustomOption: React.FC<
  OptionProps<FileStatusOptionType, boolean, GroupBase<FileStatusOptionType>>
> = (props) => {
  return (
    <components.Option {...props}>
      <FileStatusLabel status={props.label as FileDiligenceStatus} />
    </components.Option>
  );
};

const CustomSingleValue: React.FC<SingleValueProps<FileStatusOptionType>> = (props) => {
  return (
    <components.SingleValue {...props}>
      <FileStatusLabel status={props.data.label} {...props.innerProps} />
    </components.SingleValue>
  );
};

export const fromCollateralStatus = (status: CollateralStatus | null): FileDiligenceStatus => {
  if (status === null) return 'File Received';
  switch (status) {
    case CollateralStatus.IN_PROGRESS:
      return 'In Progress';
    case CollateralStatus.ADDITIONAL_QA_REQUIRED:
      return 'Additional QA Required';
    case CollateralStatus.RECEIVED:
      return 'File Received';
    case CollateralStatus.REQUIRED:
      return 'File Required';
    case CollateralStatus.COMPLETED:
      return 'File Reviewed';
    case CollateralStatus.REJECTED:
      return 'File Rejected';
    default:
      throw new Error(`Invalid status: ${status}`);
  }
};
